const PRODUCT_URL = 'https://cloudpouch.dev/';
const DEMO_URL = 'https://cloudpouch.dev/demo/';
const DOCS_URL = 'https://docs.cloudpouch.dev/';
const RELEASES_URL = 'https://github.com/CloudPouch/CloudPouch.dev/releases';

const noInputSchema = {
  type: 'object',
  properties: {},
  additionalProperties: false,
};

const tools = [
  {
    name: 'get_cloudpouch_product_info',
    description:
      'Return factual CloudPouch product, platform, privacy, and support information.',
    inputSchema: noInputSchema,
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: async () => ({
      product: 'CloudPouch',
      category: 'Local-first desktop AWS cost analysis and optimization',
      platforms: ['macOS', 'Windows', 'Linux'],
      plans:
        'Free is the basic plan, free forever with no expiry, without every feature. Every paid plan is Pro, with the full feature set. The Pro trial gives Pro for 7 days from registration, needs no credit card and switches to Free automatically the next time you start the app once it ends',
      awsAccess:
        'Uses locally configured AWS profiles and AWS SSO. Nothing is deployed into the AWS account. CloudPouch does not modify AWS resources.',
      privacy:
        'AWS cost data, resource metadata and credentials stay on the user computer during normal use. CloudPouch 2.0.0 sends no telemetry. The license check includes the application version, and error reports are sent only when the user chooses.',
      capabilities: [
        'AWS cost breakdowns by service, usage type, region, and account',
        'Savings Scan and Savings Workspace with estimated savings',
        'Agentic FinOps, an optional local MCP server that is off by default and never changes anything in the AWS account',
        'Multiple AWS accounts, organizations, profiles, and AWS SSO',
      ],
      demo: DEMO_URL,
      demoNote:
        'An interactive demo runs the Billing and Savings views of the desktop app in the browser on sample data, shown on the Pro plan, and sends nothing anywhere.',
      website: PRODUCT_URL,
      documentation: DOCS_URL,
    }),
  },
  {
    name: 'get_cloudpouch_install_options',
    description:
      'Return current official locations for installing or downloading CloudPouch.',
    inputSchema: {
      type: 'object',
      properties: {
        platform: {
          type: 'string',
          enum: ['macOS', 'Windows', 'Linux', 'unspecified'],
          description: 'The operating system to install CloudPouch on.',
        },
      },
      additionalProperties: false,
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: async ({ platform = 'unspecified' } = {}) => ({
      platform,
      homebrew:
        platform === 'macOS' || platform === 'unspecified'
          ? 'brew install cloudpouch'
          : null,
      installers: RELEASES_URL,
      registration:
        'Register in the app with an e-mail address and EULA acceptance, then enter the 6-digit code sent by e-mail. The license key then appears in the app on its own, with no key to type or paste, and a copy is sent by e-mail. A license bought directly arrives by e-mail only.',
      note: 'Use the releases page to verify the latest version and OS requirements.',
    }),
  },
  {
    name: 'get_cloudpouch_aws_access_requirements',
    description:
      'Return CloudPouch AWS access, permission, API charge, and security requirements.',
    inputSchema: noInputSchema,
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: async () => ({
      baselinePermission: 'ce:GetCostAndUsage',
      additionalPermissions:
        'Individual Cost Insights require additional read-only permissions documented by CloudPouch.',
      credentials:
        'CloudPouch reads local AWS profiles and AWS SSO in the same environment as the AWS CLI.',
      changesAwsResources: false,
      assistantConnection:
        'Optional, off by default and local only. It never changes anything in the AWS account.',
      awsCost:
        'AWS charges for Cost Explorer API requests; CloudPouch caches results to reduce repeated requests.',
      permissionReference: DOCS_URL,
    }),
  },
];

function availableModelContexts(documentObject, navigatorObject) {
  const contexts = [];

  // The scanner and early WebMCP implementations expose navigator.modelContext.
  if (
    navigatorObject &&
    navigatorObject.modelContext &&
    typeof navigatorObject.modelContext.registerTool === 'function'
  ) {
    contexts.push(navigatorObject.modelContext);
  }

  // The current Community Group draft exposes document.modelContext.
  if (
    documentObject &&
    documentObject.modelContext &&
    typeof documentObject.modelContext.registerTool === 'function' &&
    !contexts.includes(documentObject.modelContext)
  ) {
    contexts.push(documentObject.modelContext);
  }

  return contexts;
}

function registerCloudPouchTools(environment = {}) {
  const documentObject =
    environment.documentObject ||
    (typeof document === 'undefined' ? undefined : document);
  const navigatorObject =
    environment.navigatorObject ||
    (typeof navigator === 'undefined' ? undefined : navigator);
  const windowObject =
    environment.windowObject || (typeof window === 'undefined' ? undefined : window);
  const AbortControllerClass =
    environment.AbortControllerClass ||
    (typeof AbortController === 'undefined' ? undefined : AbortController);
  const contexts = availableModelContexts(documentObject, navigatorObject);

  if (contexts.length === 0 || !AbortControllerClass) {
    return { supported: false, toolCount: 0, registrationCount: 0 };
  }

  if (windowObject && windowObject.__cloudPouchWebMcpController) {
    return {
      supported: true,
      toolCount: tools.length,
      registrationCount: 0,
      alreadyRegistered: true,
    };
  }

  const controller = new AbortControllerClass();
  const registrations = [];

  contexts.forEach((modelContext) => {
    tools.forEach((tool) => {
      registrations.push(
        Promise.resolve(modelContext.registerTool(tool, { signal: controller.signal }))
      );
    });
  });

  if (windowObject) {
    windowObject.__cloudPouchWebMcpController = controller;
    windowObject.addEventListener('pagehide', () => controller.abort(), {
      once: true,
    });
  }

  return {
    supported: true,
    toolCount: tools.length,
    registrationCount: registrations.length,
    registrations,
  };
}

module.exports = { registerCloudPouchTools, tools };
